/**
 * Addresses and the places you can pick from.
 *
 * ---------------------------------------------------------------------------
 * THE SHAPES, NOT THE LOOKUPS
 * ---------------------------------------------------------------------------
 * Permissions, the fix and both geocodes are real now and live elsewhere:
 *
 *   permission   PermissionsAndroid, in `LocationScreen`
 *   the fix      @react-native-community/geolocation, in `LocationScreen`
 *   pin -> name  `reverseGeocode`, in `services/location.ts`
 *   name -> pin  `searchPlaces`,   in `services/location.ts`
 *
 * What is left here is the vocabulary they all speak - `Place`, `SavedAddress`
 * and the pure functions that turn one into the other. The address book
 * itself is not - it lives on the platform now, behind `useAddresses`.
 */

import { PlaceResult } from '../services/location';

export type Place = {
  id: string;
  /** First line, e.g. "221B Baker Street". */
  title: string;
  /** Everything after it, e.g. "London, NW1 6XE". */
  subtitle: string;
  /**
   * Where this place actually is.
   *
   * Set by anything that came from the geocoder - see `placeFromResult`. Left
   * unset by the seeded entries below, which are lines of text nobody has
   * pinned. A place without coordinates cannot be the thing a booking travels
   * on, so `LocationScreen` treats the two cases differently: a searched place
   * moves the map to itself, a seeded one leaves the pin where it was.
   */
  coords?: { lat: number; lng: number };
};

/*
 * `nearbyPlaces` and `detectedPlace` used to live here: eight lines of Baker
 * Street that stood in for an autocomplete response, with the first of them
 * exported as "what the device would have reported".
 *
 * Both are gone. Search is a real geocoder call now (see `placeFromResult`),
 * and a failed position fix sends `LocationScreen` to its `unavailable` stage
 * instead of quietly settling on 221B - which is how a phone with GPS off
 * ended up showing a confident London address a customer could book against.
 *
 * Deleted rather than left unused on purpose: an exported seed address is the
 * kind of thing that gets wired back in as a convenient default.
 */

/**
 * A pin, as an address rather than as a status.
 *
 * ---------------------------------------------------------------------------
 * "CURRENT LOCATION" IS NOT AN ADDRESS
 * ---------------------------------------------------------------------------
 * While a pin is being placed, the panel calls it "Current location", and
 * there it is the right words - it says which of the two things on screen the
 * pin came from. The moment it is SAVED it stops being true: that string went
 * into the header, the cart and the booking, where it read "Current loc…"
 * truncated beside a chevron while the street the customer had just watched
 * the app work out was nowhere.
 *
 * So the resolved parts are split back apart on the way out. The house and
 * street become the title - the line a person recognises - and the area, city
 * and postcode become the subtitle under it.
 *
 * `resolved` being null (a point in the sea, a dead network, a geocode that
 * never landed) gives "Pinned location" over the coordinates. That is honest:
 * it says where it is without claiming to know what is there, and it is still
 * not a status word pretending to be a street.
 *
 * Pure, and here rather than in `LocationScreen`, because it is the one piece
 * of that screen worth testing on its own.
 */
/**
 * The title a pin gets when nothing could be read for it.
 *
 * Exported because two other places have to RECOGNISE it: the address book,
 * which offers to repair rows that were saved in this state, and the location
 * flow, which refuses to create new ones. A duplicated string literal in
 * either would silently stop matching the day this wording changed.
 */
export const UNRESOLVED_TITLE = 'Pinned location';

/**
 * Whether an address is a bare pin rather than a readable address.
 *
 * These exist because saving one used to be allowed - the geocode failed, the
 * failure was swallowed, and "Pinned location, 22.72396, 75.90488" went into
 * the book as though it were a street. Nobody can deliver to one.
 */
export const isUnresolved = (address: SavedAddress): boolean =>
  address.place.title === UNRESOLVED_TITLE;

export const placeFromPin = (
  resolved: {
    label: string;
    line1?: string;
    area?: string;
    city?: string;
    postcode?: string;
  } | null,
  coords: { latitude: number; longitude: number },
  id = 'detected',
): Place => {
  const pin = `${coords.latitude.toFixed(5)}, ${coords.longitude.toFixed(5)}`;

  if (!resolved) {
    return { id, title: UNRESOLVED_TITLE, subtitle: pin };
  }

  const title = resolved.line1 || resolved.area || resolved.city || resolved.label;

  /*
   * Anything the title ALREADY says is dropped, not just a part equal to the
   * whole of it.
   *
   * The equality check this replaces let "Paramount Apartments, 109, Telephone
   * Nagar, Indore, Madhya Pradesh 452018, India" be followed by "Telephone
   * Nagar, Indore, 452018" - and since the two are joined into one
   * `addressLine` when the address is saved, the repeat then showed up
   * wherever that line is read: the customer's own list, the Pro's job screen,
   * the admin console.
   *
   * `streetOf` is what should keep the title short, and it now knows about the
   * country segment that used to defeat it. This is the belt to that pair of
   * braces: the geocoder is free to invent another trailing segment tomorrow,
   * and the worst that should cost is a long title - never the same words
   * twice.
   */
  const lowerTitle = title.toLowerCase();
  const rest = [resolved.area, resolved.city, resolved.postcode]
    .filter(part => !!part && !lowerTitle.includes(part.toLowerCase()))
    .join(', ');

  return {
    id,
    title,
    // Never empty: a line with nothing under it looks unfinished, and the full
    // label always carries more than its own first segment.
    subtitle: rest || (resolved.label !== title ? resolved.label : pin),
  };
};

/** The label a saved address is filed under. */
export type AddressLabel = 'Home' | 'Work' | 'Other';

export const addressLabels: AddressLabel[] = ['Home', 'Work', 'Other'];

export type SavedAddress = {
  place: Place;
  /**
   * Where the pin actually is.
   *
   * Optional because the seeded addresses have no coordinates - they are lines
   * of text somebody typed. Set by the location flow, and REQUIRED by the
   * server: `customer_addresses` stores a lat/lng and resolves a city from it,
   * so an address saved without one cannot be sent.
   */
  coords?: { lat: number; lng: number };
  label: AddressLabel;
  /**
   * What "Other" was actually called - "Mum's", "Site office", "Gym".
   *
   * Kept beside the label rather than replacing it, so the three chips stay a
   * closed set that can be matched on, while the name is free text. Only ever
   * set when `label` is "Other".
   */
  customLabel?: string;
  /** Gate code, floor, landmark - whatever the pro needs to find the door. */
  note?: string;
  /**
   * Who the professional should ask for at this address.
   *
   * Asked alongside the address because the booker and the door-opener are
   * often different people - a parent's flat, an office. The server's
   * `customer_addresses` has no column for it, so on the wire it travels
   * folded into the landmark line (see `HomeScreen`'s save path); locally it
   * stays its own field so the form can edit it separately.
   */
  contactName?: string;
  isDefault: boolean;
  /**
   * Whether we can actually send a professional here - the platform's answer,
   * not the geocoder's.
   *
   * Undefined on every address the server accepted normally, which is the
   * overwhelming majority and the reason this is optional rather than a
   * required boolean: an existing row that predates the field is not "not
   * serviceable", it is "never asked", and those must not collapse together.
   *
   * Set to `false` only on an address the platform refused to serve, so the
   * book can mark the row "Coming soon" and the checkout gate can stop it
   * without re-running a geocode per row.
   */
  serviceable?: boolean;
  /**
   * True while this address exists only on THIS device.
   *
   * The server rejects an out-of-area pin outright, so an address in a city
   * we have not reached yet has no server id and cannot be fetched back. It
   * is held locally instead - see `pendingAddresses` in `data/storage.ts` -
   * and this flag is what tells the two apart, because everything else about
   * the two rows looks identical on screen.
   *
   * It matters at delete time: a local row must not be sent to
   * `DELETE /customers/me/addresses/:id`, which would 404 on an id the
   * server has never issued.
   */
  localOnly?: boolean;
};

/**
 * What the app shows before any address has been chosen.
 *
 * A sentinel rather than an empty string, because it is also the prompt: the
 * header reads "Set your location" and tapping it starts the flow. Compared
 * against by the cart, which cannot let a booking proceed without a real one -
 * so it lives here rather than being spelled out in each screen.
 */
export const NO_ADDRESS = 'Set your location';

/**
 * What to call an address.
 *
 * "Other" is a bucket, not a name - two addresses both filed under it are
 * indistinguishable in a list. Where one was typed, that wins.
 */
export const addressName = (address: SavedAddress): string =>
  address.label === 'Other' && address.customLabel?.trim()
    ? address.customLabel.trim()
    : address.label;

/** One line, the way the home header shows it. */
export const formatAddress = (address: SavedAddress): string =>
  `${address.place.title}, ${address.place.subtitle}`;

/**
 * What to call the address currently in use, given only its formatted line.
 *
 * The cart is handed a string rather than a `SavedAddress` - it only ever
 * needed to display it - so the name has to be recovered by matching. Returns
 * undefined for an address that is not on file, which is any address the
 * location flow just produced before it has been saved. Callers must handle
 * that rather than assume "Home".
 */
export const addressNameFor = (
  line: string,
  book: SavedAddress[] = [],
): string | undefined => {
  const found = book.find(address => formatAddress(address) === line);
  return found ? addressName(found) : undefined;
};

/**
 * A search result, as a `Place`.
 *
 * ---------------------------------------------------------------------------
 * WHY THE COORDINATES COME ACROSS TOO
 * ---------------------------------------------------------------------------
 * This used to be a contains-match over `nearbyPlaces`, which meant a searched
 * address was eight lines of London text with no pin behind it. The screen
 * then saved it against whatever coordinates the map happened to be showing -
 * so picking "Regent's Park" filed the customer's *current* pin under the name
 * of a park they had merely typed.
 *
 * Carrying `coords` through is what closes that: the map moves to the result,
 * and the address that gets saved is the one the pin is actually on.
 */
export const placeFromResult = (result: PlaceResult): Place => ({
  id: result.id,
  title: result.title,
  // The geocoder can return a bare name with nothing under it. A row with an
  // empty second line reads as a rendering fault, so fall back to the pin.
  subtitle:
    result.subtitle || `${result.lat.toFixed(5)}, ${result.lng.toFixed(5)}`,
  coords: { lat: result.lat, lng: result.lng },
});
